import { selectChangeMessageForWebFacets } from '@UI/aside/asideWebFacets/asideWebFacetsSelectors';
import { selectChangeMessageForAsideWebOldUrls } from '@UI/aside/asideWebOldUrls/asideWebOldUrlsSelectors';
import { selectChangeMessageForWebType } from '@UI/aside/asideWebType/asideWebTypeSelectors';
import { selectChangeMessageForWebPageCheckbox } from '@newStore/documentUI/changeMessageSelectors/selectChangeMessageForWebPageCheckbox';
import { selectChangeMessageForWebPageDate } from '@newStore/documentUI/changeMessageSelectors/selectChangeMessageForWebPageDate';
import { selectChangeMessageForWebPageHtmlString } from '@newStore/documentUI/changeMessageSelectors/selectChangeMessageForWebPageHtmlString';
import { noDuplicateWebPaths } from '@newStore/validation/validationRules/noDuplicateWebPaths';
import {
  selectIsBannerDateValid,
  selectIsBannerFieldRequired,
  selectIsContentTypeValidForWebTemplateRule,
  selectIsValidBannerUrl,
  selectIsWebTemplateForChildValidRule,
  selectOldUrlsNoUnAllowedCharsRule,
  selectPathFormatValidRule,
  selectPathNoReservedWordsValidRule,
  selectPathNoUnAllowedCharsRule,
  selectPathUniqueWithinDocumentRule,
} from '@newStore/validation/validationRules/webPageValidationRules';
import {
  type EditAsideWebBannerCallToAction,
  type EditAsideWebBannerFromOrTo,
  type EditAsideWebBannerHighlight,
  type EditAsideWebBannerSubtitle,
  type EditAsideWebBannerTitle,
  type EditAsideWebBannerUrl,
  type EditAsideWebFacets,
  type EditAsideWebOldUrls,
  type EditAsideWebPath,
  type EditAsideWebType,
  RequiredType,
  type WebTabComponent,
} from '@nodeTypeConfig/configTypes';

const asideWebPath: EditAsideWebPath = {
  webPage: true, // used to easily differentiate between webPage and edit components
  component: 'webPath',
  reactComponent: 'AsideWebSimpleInput',
  property: 'path',
  label: 'Pad',
  definiteArticle: true,
  asideChangeMessageSelector: selectChangeMessageForWebPageHtmlString,
  validations: [
    noDuplicateWebPaths,
    selectPathNoReservedWordsValidRule,
    selectPathNoUnAllowedCharsRule,
    selectPathUniqueWithinDocumentRule,
    selectPathFormatValidRule,
  ],
  required: RequiredType.ERROR,
};

export const asideWebTypeNotRequired: EditAsideWebType = {
  webPage: true, // used to easily differentiate between webPage and edit components
  component: 'webType',
  reactComponent: 'AsideWebType',
  property: 'template.href',
  label: 'Type',
  definiteArticle: true,
  asideChangeMessageSelector: selectChangeMessageForWebType,
  validations: [selectIsContentTypeValidForWebTemplateRule, selectIsWebTemplateForChildValidRule],
};

const asideWebFacets: EditAsideWebFacets = {
  webPage: true, // used to easily differentiate between webPage and edit components
  component: 'webFacets',
  reactComponent: 'AsideWebFacets',
  property: 'options.facets',
  label: 'Facetten',
  asideChangeMessageSelector: selectChangeMessageForWebFacets,
};

export const asideWebType: EditAsideWebType = {
  ...asideWebTypeNotRequired,
  required: RequiredType.ERROR,
};

const asideWebOldUrls: EditAsideWebOldUrls = {
  label: "Vorige url's",
  component: 'webOldUrls',
  webPage: true,
  reactComponent: 'AsideWebOldUrls',
  property: 'oldLocations',
  asideChangeMessageSelector: selectChangeMessageForAsideWebOldUrls,
  validations: [noDuplicateWebPaths, selectOldUrlsNoUnAllowedCharsRule],
};

export const asideWebBasicConfiguration: WebTabComponent[] = [
  asideWebType,
  asideWebPath,
  asideWebOldUrls,
];

const asideWebBannerHighlight: EditAsideWebBannerHighlight = {
  webPage: true,
  component: 'webBannerHighlight',
  reactComponent: 'AsideWebSimpleCheckbox',
  property: 'options.banner.highlight',
  label: 'Call to action banner',
  asideChangeMessageSelector: selectChangeMessageForWebPageCheckbox,
  validations: [],
};

const asideWebBannerTitle: EditAsideWebBannerTitle = {
  webPage: true,
  component: 'webBannerTitle',
  reactComponent: 'AsideWebSimpleInput',
  property: 'options.banner.title',
  label: 'Titel',
  asideChangeMessageSelector: selectChangeMessageForWebPageHtmlString,
  validations: [selectIsBannerFieldRequired],
};

const asideWebBannerCallToAction: EditAsideWebBannerCallToAction = {
  webPage: true,
  component: 'webBannerCallToAction',
  reactComponent: 'AsideWebSimpleInput',
  property: 'options.banner.cta',
  label: 'Call to action',
  asideChangeMessageSelector: selectChangeMessageForWebPageHtmlString,
  validations: [selectIsBannerFieldRequired],
};

const asideWebBannerSubtitle: EditAsideWebBannerSubtitle = {
  webPage: true,
  component: 'webBannerSubtitle',
  reactComponent: 'AsideWebSimpleInput',
  property: 'options.banner.subtitle',
  label: 'Ondertekst',
  asideChangeMessageSelector: selectChangeMessageForWebPageHtmlString,
  validations: [selectIsBannerFieldRequired],
};

const asideWebBannerUrl: EditAsideWebBannerUrl = {
  webPage: true,
  component: 'webBannerUrl',
  reactComponent: 'AsideWebSimpleInput',
  property: 'options.banner.url',
  label: 'URL',
  asideChangeMessageSelector: selectChangeMessageForWebPageHtmlString,
  validations: [selectIsBannerFieldRequired, selectIsValidBannerUrl],
};

const asideWebBannerFrom: EditAsideWebBannerFromOrTo = {
  webPage: true,
  component: 'webBannerFrom',
  reactComponent: 'AsideWebDatePicker',
  property: 'options.banner.from',
  label: 'Van',
  asideChangeMessageSelector: selectChangeMessageForWebPageDate,
  validations: [selectIsBannerFieldRequired, selectIsBannerDateValid],
  width: 6,
};

const asideWebBannerTo: EditAsideWebBannerFromOrTo = {
  webPage: true,
  component: 'webBannerTo',
  reactComponent: 'AsideWebDatePicker',
  property: 'options.banner.to',
  label: 'Tot',
  asideChangeMessageSelector: selectChangeMessageForWebPageDate,
  validations: [selectIsBannerFieldRequired],
  width: 6,
};

export const asideWebBannerConfiguration: WebTabComponent[] = [
  asideWebBannerHighlight,
  asideWebBannerTitle,
  asideWebBannerSubtitle,
  asideWebBannerCallToAction,
  asideWebBannerUrl,
  asideWebBannerFrom,
  asideWebBannerTo,
];

export const asideWebFacetConfiguration: WebTabComponent[] = [asideWebFacets];
